/* eslint-disable @typescript-eslint/no-explicit-any */
import GenericCard from "@ui-components/Card/GenericCard";
import { ReactComponent as IconSettings } from "@shared/assets/Icons/IconSettings.svg";
import React, { useEffect, useMemo, useState } from "react";
import { GenericTable } from "@ui-components/Table/GenericTable";
import { AlertItemHistory, makeAlertColumns } from "./NotificationsHistoryData";
import GenericButton from "@ui-components/Button/GenericButton";
import { useNavigate } from "react-router-dom";
import { fetchAlertsList } from "../requests/request";
import { useAxiosAuth0 } from "@shared/config/useAxiosAuth0";

import { GenericFilter } from "@ui-components/Filter/GenericFilter";
import { useProcessingOverlay } from "@shared/hooks/useProcessingOverlay";
import GenericModal from "@ui-components/Modal/GenericModal";
import { GenericTag } from "@ui-components/Tag/GenericTag";
import { useAppSelector } from "@redux/hooks";

export default function NotificationsHistory() {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<AlertItemHistory[]>([]);
  const api = useAxiosAuth0();

  const [searchTerm, setSearchTerm] = useState("");
  const [searchColumn, setSearchColumn] = useState("All");
  const [isFetching, setIsFetching] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  const overlaySpinner = useProcessingOverlay(isFetching);
  //Modal logic
  const [selectedAlert, setSelectedAlert] = useState<AlertItemHistory | null>(
    null
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { tenantId, partnerId } = useAppSelector(state => state.selection);
  const isViewingAllCustomers = tenantId === partnerId;
  

  const handleView = (alert: AlertItemHistory) => {
    setSelectedAlert(alert);
    setIsModalOpen(true);
  };

  const handleClose = () => {
    setIsModalOpen(false);
    setSelectedAlert(null);
  };

  const columns = useMemo(() => {
    let cols = makeAlertColumns(handleView);
    if (!isViewingAllCustomers) {
      cols = cols.filter(col => col.accessor !== 'tenantName');
    }
    return cols;
  }, [handleView, isViewingAllCustomers]);

  // const columns = useMemo(() => makeAlertColumns(handleView), [handleView]);

  // Filtered data
  const filteredData = useMemo(() => {
    const term = searchTerm.toLowerCase();

    return notifications.filter((item) => {
      if (searchColumn === "All") {
        return [
          item.alertName.toLowerCase().includes(term),
          item.description.toLowerCase().includes(term),
          item.severity.toLowerCase().includes(term),
          item.createdAt.toLowerCase().includes(term),
          item.tenantName.toLowerCase().includes(term),
        ].some(Boolean);
      }

      switch (searchColumn) {
        case "Name":
          return item.alertName.toLowerCase().includes(term);
        case "Description":
          return item.description.toLowerCase().includes(term);
        case "Severity":
          return item.severity.toLowerCase().includes(term);
        case "Created At":
          return item.createdAt.toLowerCase().includes(term);
        case "Customer Name":
          return item.tenantName.toLowerCase().includes(term);
        default:
          return true;
      }
    });
  }, [notifications, searchTerm, searchColumn]);

  const columnOptions = [
    "All",
    "Name",
    "Description",
    "Severity",
    "Created At",
    "Customer Name"
  ];

  useEffect(() => {
    const fetchAlerts = async () => {
      try {
        setIsFetching(true);
        const response = await fetchAlertsList(api);
        const events: any[] = response.data?.data?.alertEvents || [];

        const formatted: AlertItemHistory[] = events.map((a) => ({
          id: a.id,
          alertName: a.alertName ?? '-',
          severity: a.severity ?? '-',
          tenantName: a.tenantInfo?.tenantName ?? '-',
          resourceName: a.resourceName ?? '-',
          description: a.description ?? "No description",
          createdAt: new Date(a.createdAt).toLocaleString("en-US", {
            dateStyle: "medium",
            timeStyle: "short",
          }),
          updatedAt: new Date(a.updatedAt).toLocaleString("en-US", {
            dateStyle: "medium",
            timeStyle: "short",
          }),
          isActive: a.isActive,
          trigger: a.trigger,
        }));

        setNotifications(formatted);
      } catch (err) {
        console.error("Error loading alert history", err);
      } finally {
        setIsFetching(false);
        setHasFetched(true);
      }
    };

    fetchAlerts();
  }, [api]);

  if (!hasFetched) {
    return overlaySpinner;
  }

  return (
    <div>
      <GenericCard
        headerContent="Historical data of notifications"
        isCollapsible={false}
        rightContent={
          <GenericButton
            variant="brand"
            label="Notifications Management"
            icon={<IconSettings className="w-5 h-5" />}
            onClick={() => navigate("/kontrol/notifications-management")}
          />
        }
        bodyContent={
          <>
            <div className="pb-5 space-y-4">
              <GenericFilter
                searchColumn={searchColumn}
                onSearchColumnChange={setSearchColumn}
                searchTerm={searchTerm}
                onSearchTermChange={setSearchTerm}
                columnOptions={columnOptions}
              />
            </div>
            <GenericTable
              columns={columns}
              data={filteredData}
              initialRowsPerPage={5}
              rowsPerPageOptions={[5, 10]}
              width="100%"
              tableClassName="bg-semantic-bg-primary"
            />
            {overlaySpinner}
            <GenericModal
              isOpen={isModalOpen}
              onClose={handleClose}
              title={selectedAlert?.alertName}
              blurBackdrop
            >
              {selectedAlert && (
                <div className="flex-column w-full self-start border-t py-5">
                  <p className="text-sm font-medium text-semantic-text-secondary">
                    {selectedAlert.description ?? "-"}
                  </p>
                  <div className="flex p-5 justify-between gap-4">
                    <GenericCard
                      isCollapsible={false}
                      bodyContent={
                        <div className="flex-wrap justify-between w-full py-1">
                          {/* Created At */}
                          <div className="flex justify-between w-full border-b py-2">
                            <h3 className="text-sm font-medium text-semantic-text-primary">
                              Created at
                            </h3>
                            <p className="text-sm font-medium text-semantic-text-secondary">
                              {selectedAlert.createdAt ?? "-"}
                            </p>
                          </div>
                          {/* Updated At */}
                          <div className="flex justify-between w-full border-b py-2">
                            <h3 className="text-sm font-medium text-semantic-text-primary">
                              Updated at
                            </h3>
                            <p className="text-sm font-medium text-semantic-text-secondary">
                              {selectedAlert.updatedAt ?? "-"}
                            </p>
                          </div>
                          {/* Severity */}
                          <div className="flex justify-between w-full border-b py-2">
                            <h3 className="text-sm font-medium text-semantic-text-primary">
                              Severity
                            </h3>
                            <GenericTag
                              label={
                                selectedAlert.severity.charAt(0).toUpperCase() +
                                selectedAlert.severity.slice(1)
                              }
                              variant={
                                selectedAlert.severity === "critical"
                                  ? "error"
                                  : "warning"
                              }
                              size="small"
                              rounded="full"
                              className="w-24 justify-center text-center"
                            />
                          </div>
                          {/* Status */}
                          <div className="flex justify-between w-full border-b py-2">
                            <h3 className="text-sm font-medium text-semantic-text-primary">
                              Status
                            </h3>
                            <GenericTag
                              label={
                                selectedAlert.isActive ? "Active" : "Inactive"
                              }
                              variant={
                                selectedAlert.isActive ? "success" : "error"
                              }
                              size="small"
                              rounded="full"
                              className="w-24 justify-center text-center"
                            />
                          </div>
                          {/* Trigger */}
                          <div className="flex justify-between w-full border-b py-2">
                            <h3 className="text-sm font-medium text-semantic-text-primary">
                              Trigger
                            </h3>
                            <p className="text-sm font-medium text-semantic-text-secondary">
                              {selectedAlert.trigger
                                ? selectedAlert.trigger
                                    .charAt(0)
                                    .toUpperCase() +
                                  selectedAlert.trigger.slice(1)
                                : "-"}
                            </p>
                          </div>
                        </div>
                      }
                    />
                  </div>
                </div>
              )}
            </GenericModal>
          </>
        }
      />
    </div>
  );
}
